import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomOrderEntry } from '@cart/const';
import { CustomActiveCartService } from '@cart/data-access';
import { COMPETITORS_LIST, CompetitorsFromTown, SelectedPrice } from '@product/const';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ManualPriceService {
  forms$: BehaviorSubject<Map<string, ManualPriceFormItem>> = new BehaviorSubject(new Map());

  constructor(private customActiveCartService: CustomActiveCartService) {}

  // создаем инстанс класса с формой ручной цены и пихаем в мап в сабджект
  createForm(unitPrice: SelectedPrice, productCode: string, baseStoreCode: string): void {
    this.forms$.pipe(take(1)).subscribe((formsObj) => {
      const key = this.keyGenerator(unitPrice.unit?.code, productCode, baseStoreCode);

      if (!formsObj.has(key)) {
        const form = new FormGroup({
          price: new FormControl('', [Validators.min(unitPrice.minValue)]),
          competitor: new FormControl('', [Validators.required]),
        });

        const formItem = new ManualPriceFormItem(
          form,
          unitPrice.minValue,
          this.customActiveCartService.getEntry(productCode),
          unitPrice.unit?.code,
        );

        formsObj.set(key, formItem);

        this.forms$.next(formsObj);
      }
    });
  }

  getPriceItem(key: string): Observable<ManualPriceFormItem | undefined> {
    return this.forms$.pipe(map((formMap) => formMap.get(key)));
  }

  removeAll(): void {
    this.forms$.pipe(take(1)).subscribe((formsObj: Map<string, ManualPriceFormItem>) => {
      formsObj.forEach((formItem: ManualPriceFormItem) => {
        formItem.destroy();
      });
    });

    this.forms$.next(new Map());
  }

  keyGenerator(unitCode: string, productCode: string, baseStoreCode: string): string {
    return `${unitCode}-${productCode}-${baseStoreCode}`;
  }

  getCompetitorsArray(cityName: string): Array<string> {
    const competitorsByTown = COMPETITORS_LIST.filter(
      (competitorsFromTown: CompetitorsFromTown) => competitorsFromTown.sourceTown === cityName,
    );
    // Если нет конкурентов в городе, берем первый из константы по умолчанию
    if (competitorsByTown.length === 0) return COMPETITORS_LIST[0].competitors;

    return competitorsByTown[0].competitors;
  }
}

// Класс формы ввода ручной цены
// знает про свою форму, сам разруливает свою уебскую логику ручной цены
export class ManualPriceFormItem {
  form: FormGroup;
  // минимальная цена
  minimal: number;

  unitCode: string;
  // соответствует ли текущая цена минимальной
  isMinimumPrice$ = new BehaviorSubject<boolean>(false);
  // заполнено ли поле
  isPriceEmpty$ = new BehaviorSubject<boolean>(true);
  // удовлетворяет ли цена условию успеха
  isPriceSuccess$ = new ReplaySubject<boolean>(1);
  // Форма может стать не валидной только после взаимодейтсвия с полями
  isInvalid$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  customOrderEntry$: Observable<CustomOrderEntry | undefined>;

  subscription = new Subscription();

  constructor(
    formGroup: FormGroup,
    minimal: number,
    customOrderEntry$: Observable<CustomOrderEntry | undefined>,
    unitCode: string,
  ) {
    this.form = formGroup;
    this.minimal = minimal;
    this.customOrderEntry$ = customOrderEntry$;
    this.unitCode = unitCode;

    this.subscription.add(
      this.customOrderEntry$.subscribe((entry) => {
        if (entry) {
          this.disable(entry.saleUnit.code === this.unitCode && entry.basePrice.value);
        } else {
          this.enable();
        }
      }),
    );

    // костыль.
    // нам нужно при обновлении страницы получать isPriceEmpty и isMinimumPrice в шаблоне компонента.
    // если просто поставить next в конструкторе, работать не будет,
    // похоже у нас идет  задержка для подписчиков, может из за конструктора
    // лучше отрефакторить всю логику TODO https://jira.sdvor.com/browse/SPA-460
    setTimeout(() => {
      this.isPriceEmpty$.next(this.emptyCondition());
      this.isMinimumPrice$.next(this.minimalPriceCondition());
    }, 0);

    this.subscription.add(
      this.form.get('price').valueChanges.subscribe(() => {
        this.isPriceSuccess$.next(this.successCondition());
      }),
    );

    this.subscription.add(
      this.form.get('price').valueChanges.subscribe(() => {
        this.isMinimumPrice$.next(this.minimalPriceCondition());
      }),
    );

    this.subscription.add(
      this.form.get('price').valueChanges.subscribe(() => {
        this.isPriceEmpty$.next(this.emptyCondition());
      }),
    );

    this.subscription.add(
      this.form.valueChanges.subscribe((formValue) => {
        this.isInvalid$.next(this.invalidCondition(formValue));
      }),
    );
  }

  successCondition(): boolean {
    const control = this.form.get('price');

    return control?.valid && control?.value !== '';
  }

  minimalPriceCondition(): boolean {
    const control = this.form.get('price');

    return +control?.value === this.minimal;
  }

  emptyCondition(): boolean {
    return this.form.get('price').value === '' || this.form.get('price').value === null;
  }

  invalidCondition(formValue: { price: string; competitor: string }): boolean {
    if (formValue.price !== '' && formValue.price !== null) {
      return true;
    } else {
      return false;
    }
  }

  disable(value?: number): void {
    const controlPrice = this.form.get('price');
    const controlCompetitor = this.form.get('competitor');
    if (value) {
      controlPrice.patchValue(value);
    }
    controlPrice.disable();
    controlCompetitor.disable();
  }

  enable(): void {
    const controlPrice = this.form.get('price');
    const controlCompetitor = this.form.get('competitor');
    controlPrice.enable();
    controlCompetitor.enable();
  }

  destroy(): void {
    this.subscription.unsubscribe();
  }
}
